import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { RÉGLAGES_GHOST } from '@vigie/shared';
import type { TypeRéglage } from '@vigie/shared';
import { configuration, sitesRéglages } from '../api.js';
import { RéglagesRecommandés } from '../components/ReglagesRecommandes.js';
import type {
  AperçuConfig,
  RésultatConfig,
  RévisionConfig,
  SourceConfig,
  SourceConfigKind,
} from '../api.js';
import {
  appliquerÉditions,
  estCléEnvironnement,
  groupeDeClé,
  ordonnerGroupes,
} from './edition-config.js';

/**
 * CodeMirror est chargé à la demande.
 *
 * C'est la plus grosse dépendance du frontend, et seul le mode texte de cet écran s'en sert :
 * l'inclure dans le lot principal ferait payer son téléchargement au tableau de bord, qui est la
 * page ouverte en permanence. Le fragment est servi par le plan de contrôle, comme le reste.
 */
const EditeurJson = lazy(async () => ({
  default: (await import('../components/EditeurJson.js')).EditeurJson,
}));

interface Props {
  organizationId: string;
  siteId: string;
  siteNom: string;
  /** Rôle dans l'organisation : la lecture suffit en `operator`, l'écriture exige `admin`. */
  rôle: string;
  modeRedémarrage: 'driver' | 'external';
  consigneRedémarrage: string | null;
  onRetour: () => void;
}

/** Consigne par défaut, celle des hébergements Node.js d'Infomaniak. */
const CONSIGNE_PAR_DÉFAUT =
  'Manager Infomaniak → Hébergement → le site Node.js → bouton « Redémarrer ».';

const LIBELLÉS_SOURCE: Record<SourceConfigKind, string> = {
  json_file: 'Fichier de configuration',
  compose_env: 'Variables du compose',
  env_file: 'Fichier d’environnement',
};

type Mode = 'formulaire' | 'recommandés' | 'texte';

/**
 * Type attendu de chaque clé du catalogue.
 *
 * Indispensable pour une clé absente du fichier : sans valeur d'origine dont s'inspirer, un
 * booléen serait écrit `"false"` — une chaîne, que JavaScript juge vraie — et le réglage ferait
 * l'inverse de ce qu'on demande.
 */
const TYPES_RECOMMANDÉS: Record<string, TypeRéglage> = Object.fromEntries(
  RÉGLAGES_GHOST.flatMap((groupe) =>
    groupe.réglages.map((réglage) => [réglage.chemin, réglage.type] as const),
  ),
);

/**
 * Éditeur de configuration d'un site.
 *
 * Trois précautions structurent l'écran, chacune répondant à une manière concrète de casser un
 * site en production :
 *
 * - les secrets arrivent masqués et se révèlent un par un, chaque révélation étant journalisée ;
 * - rien n'est écrit sans une prévisualisation confirmée, calculée par le serveur sur la
 *   configuration réellement en place ;
 * - une clé dont la valeur effective vient de l'environnement est signalée, parce que l'éditer
 *   dans le fichier ne changerait rien au comportement du site.
 */
export function Configuration({
  organizationId,
  siteId,
  siteNom,
  rôle,
  modeRedémarrage,
  consigneRedémarrage,
  onRetour,
}: Props) {
  const peutModifier = rôle === 'admin';
  const [externe, setExterne] = useState(modeRedémarrage === 'external');
  const [consigne, setConsigne] = useState(consigneRedémarrage ?? CONSIGNE_PAR_DÉFAUT);
  const [réglageEnregistré, setRéglageEnregistré] = useState<string | null>(null);

  /** Enregistre qui redémarre ce site. */
  async function enregistrerRéglage(activé: boolean) {
    setExterne(activé);
    const { status, data } = await sitesRéglages.modeRedémarrage(organizationId, siteId, {
      restartMode: activé ? 'external' : 'driver',
      restartHint: activé ? consigne : null,
    });
    if (status >= 400) {
      setErreur(data.error ?? 'Réglage refusé');
      setExterne(!activé);
      return;
    }
    setRéglageEnregistré(
      activé
        ? 'Vigie ne tentera plus de redémarrer ce site : elle écrira et te dira quoi faire.'
        : 'Vigie redémarrera ce site elle-même.',
    );
  }

  const [sources, setSources] = useState<SourceConfig[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [indexSource, setIndexSource] = useState(0);
  const [mode, setMode] = useState<Mode>('formulaire');
  const [éditions, setÉditions] = useState<Record<string, string>>({});
  const [texte, setTexte] = useState('');
  const [révélées, setRévélées] = useState<Record<string, string>>({});
  const [aperçu, setAperçu] = useState<AperçuConfig | null>(null);
  const [résultat, setRésultat] = useState<RésultatConfig | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [révisions, setRévisions] = useState<RévisionConfig[] | null>(null);

  const source = sources?.[indexSource];

  const charger = useCallback(async () => {
    try {
      const { status, data } = await configuration.lire(organizationId, siteId);
      if (status >= 400) {
        setErreur(data.error ?? 'Lecture de la configuration impossible');
        return;
      }
      setSources(data.sources);
      setÉditions({});
      setRévélées({});
      setAperçu(null);
    } catch {
      setErreur('Service injoignable');
    }
  }, [organizationId, siteId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Le mode texte part du contenu brut de la source affichée, secrets masqués compris : ils sont
  // réinjectés côté serveur, donc les laisser masqués n'écrase rien.
  useEffect(() => {
    if (source !== undefined) setTexte(source.rawContent);
  }, [source]);

  const nombreModifications = mode === 'texte' ? -1 : Object.keys(éditions).length;

  /**
   * Avertissements du dernier aperçu, rangés par groupe.
   *
   * Affichés dans le panneau des réglages, à côté du champ qui les fait naître, et non seulement
   * dans le résumé : « statistiques inactives » n'aide que si l'on voit quelle clé manque.
   */
  const avertissementsParGroupe = useMemo(() => {
    const parGroupe: Record<string, string[]> = {};
    for (const avertissement of aperçu?.warnings ?? []) {
      (parGroupe[avertissement.groupe] ??= []).push(avertissement.message);
    }
    return parGroupe;
  }, [aperçu]);

  function contenuÀSoumettre(courante: SourceConfig): unknown {
    if (mode === 'texte') return JSON.parse(texte);
    return appliquerÉditions(JSON.parse(courante.rawContent), éditions, TYPES_RECOMMANDÉS);
  }

  async function révéler(chemin: string) {
    if (source === undefined) return;
    setEnCours('révélation');
    try {
      const { status, data } = await configuration.révéler(organizationId, siteId, {
        source: source.kind,
        remotePath: source.remotePath,
        path: chemin,
      });
      if (status >= 400) {
        setErreur(data.error ?? 'Révélation impossible');
        return;
      }
      setRévélées((précédentes) => ({ ...précédentes, [chemin]: data.value }));
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function prévisualiser() {
    if (source === undefined) return;
    setErreur(null);
    setRésultat(null);

    let contenu: unknown;
    try {
      contenu = contenuÀSoumettre(source);
    } catch {
      // Faute de syntaxe en mode texte : inutile d'aller déranger le serveur pour l'apprendre.
      setErreur('Le contenu n’est pas un JSON valide : corrige la syntaxe avant de continuer.');
      return;
    }

    setEnCours('prévisualisation');
    try {
      const { status, data } = await configuration.prévisualiser(organizationId, siteId, {
        source: source.kind,
        remotePath: source.remotePath,
        content: contenu,
      });
      if (status >= 400) {
        setAperçu(status === 422 ? data : null);
        if (status !== 422) setErreur(data.error ?? 'Prévisualisation impossible');
        return;
      }
      setAperçu(data);
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  async function appliquer() {
    if (source === undefined) return;
    setEnCours('application');
    setErreur(null);
    try {
      const { status, data } = await configuration.appliquer(organizationId, siteId, {
        source: source.kind,
        remotePath: source.remotePath,
        content: contenuÀSoumettre(source),
      });
      setAperçu(null);
      if (status >= 400) {
        setRésultat(status === 422 ? data : null);
        if (status !== 422) setErreur(data.error ?? 'Application impossible');
        return;
      }
      setRésultat(data);
      // La configuration en place a changé : on repart de l'état réel du serveur.
      await charger();
      if (révisions !== null) await chargerRévisions();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  const chargerRévisions = useCallback(async () => {
    const { status, data } = await configuration.révisions(organizationId, siteId);
    if (status >= 400) {
      setErreur(data.error ?? 'Historique indisponible');
      return;
    }
    setRévisions(data.revisions);
  }, [organizationId, siteId]);

  async function restaurer(revisionId: string) {
    setEnCours('restauration');
    setErreur(null);
    try {
      const { status, data } = await configuration.restaurer(organizationId, siteId, revisionId);
      if (status >= 400) {
        setErreur(data.error ?? 'Restauration impossible');
        return;
      }
      setRésultat(data);
      await charger();
      await chargerRévisions();
    } catch {
      setErreur('Service injoignable');
    } finally {
      setEnCours(null);
    }
  }

  const groupes = useMemo(() => {
    if (source === undefined) return [];
    const par: Record<string, string[]> = {};
    for (const chemin of Object.keys(source.keys)) {
      const groupe = groupeDeClé(chemin);
      (par[groupe] ??= []).push(chemin);
    }
    return ordonnerGroupes(Object.keys(par)).map((groupe) => ({
      nom: groupe,
      clés: (par[groupe] ?? []).sort((a, b) => a.localeCompare(b, 'fr')),
    }));
  }, [source]);

  if (erreur !== null && sources === null) return <p className="erreur">{erreur}</p>;
  if (sources === null) return <p className="aide">Lecture de la configuration…</p>;
  if (source === undefined) {
    return (
      <section className="carte">
        <button type="button" className="lien" onClick={onRetour}>
          ← Retour au site
        </button>
        <p className="aide">Aucune source de configuration lisible sur ce site.</p>
      </section>
    );
  }

  return (
    <section className="carte">
      <button type="button" className="lien" onClick={onRetour}>
        ← Retour au site
      </button>
      <h2>Configuration — {siteNom}</h2>

      {!peutModifier && (
        <p className="avertissement">
          Lecture seule : la modification de la configuration est réservée aux administratrices et
          administrateurs.
        </p>
      )}
      {erreur !== null && <p className="erreur">{erreur}</p>}

      {sources.length > 1 && (
        <div className="onglets" role="tablist">
          {sources.map((candidate, index) => (
            <button
              key={`${candidate.kind}:${candidate.remotePath}`}
              type="button"
              role="tab"
              aria-selected={index === indexSource}
              className={index === indexSource ? 'onglet onglet--actif' : 'onglet'}
              onClick={() => {
                setIndexSource(index);
                setÉditions({});
                setRévélées({});
                setAperçu(null);
              }}
            >
              {LIBELLÉS_SOURCE[candidate.kind]}
            </button>
          ))}
        </div>
      )}
      <p className="aide">
        <code>{source.remotePath}</code>
      </p>

      <div className="bascule-mode">
        <button
          type="button"
          className={mode === 'formulaire' ? 'onglet onglet--actif' : 'onglet'}
          onClick={() => setMode('formulaire')}
        >
          Formulaire
        </button>
        <button
          type="button"
          className={mode === 'recommandés' ? 'onglet onglet--actif' : 'onglet'}
          onClick={() => setMode('recommandés')}
        >
          Réglages recommandés
        </button>
        <button
          type="button"
          className={mode === 'texte' ? 'onglet onglet--actif' : 'onglet'}
          onClick={() => setMode('texte')}
        >
          Texte brut
        </button>
      </div>

      {mode === 'recommandés' ? (
        <RéglagesRecommandés
          source={source}
          éditions={éditions}
          setÉditions={setÉditions}
          révélées={révélées}
          onRévéler={(chemin) => void révéler(chemin)}
          peutModifier={peutModifier}
          enCours={enCours !== null}
          avertissements={avertissementsParGroupe}
        />
      ) : mode === 'formulaire' ? (
        <div className="config-groupes">
          {groupes.map((groupe) => (
            <fieldset key={groupe.nom} className="config-groupe">
              <legend>{groupe.nom}</legend>
              {groupe.clés.map((chemin) => {
                const valeur = source.keys[chemin];
                const origine = source.origins[chemin];
                const imposéeParEnv = estCléEnvironnement(chemin);
                const écrasée = origine?.effective === false;
                const affichée = éditions[chemin] ?? révélées[chemin] ?? valeur?.value ?? '';
                const masquéeEncore =
                  valeur?.masked === true &&
                  révélées[chemin] === undefined &&
                  éditions[chemin] === undefined;

                return (
                  <div key={chemin} className="config-champ">
                    <label htmlFor={`config-${chemin}`}>
                      <span className="config-chemin">{chemin}</span>
                      {écrasée && (
                        <span className="etiquette etiquette--alerte">
                          écrasée par l’environnement
                        </span>
                      )}
                      {imposéeParEnv && (
                        <span className="etiquette">
                          valeur effective, imposée par l’environnement
                        </span>
                      )}
                    </label>
                    <div className="config-saisie">
                      <input
                        id={`config-${chemin}`}
                        type="text"
                        value={affichée}
                        readOnly={!peutModifier || imposéeParEnv}
                        onChange={(événement) =>
                          setÉditions((précédentes) => ({
                            ...précédentes,
                            [chemin]: événement.target.value,
                          }))
                        }
                      />
                      {masquéeEncore && peutModifier && (
                        <button
                          type="button"
                          className="lien"
                          disabled={enCours !== null}
                          onClick={() => void révéler(chemin)}
                        >
                          Révéler
                        </button>
                      )}
                    </div>
                    {écrasée && éditions[chemin] !== undefined && (
                      <p className="avertissement">
                        Cette clé est définie ici mais une variable d’environnement l’emporte : la
                        modifier n’aura aucun effet tant que la variable existe. Édite plutôt la
                        variable, dans l’onglet des variables du compose.
                      </p>
                    )}
                  </div>
                );
              })}
            </fieldset>
          ))}
        </div>
      ) : (
        <>
          <p className="aide">
            Les secrets restent masqués. Une valeur laissée masquée est conservée telle quelle ; la
            remplacer par du texte la remplace pour de bon.
          </p>
          <Suspense fallback={<p className="aide">Chargement de l’éditeur…</p>}>
            <EditeurJson valeur={texte} onChange={setTexte} lectureSeule={!peutModifier} />
          </Suspense>
        </>
      )}

      {peutModifier && (
        <div className="actions">
          <button
            type="button"
            disabled={enCours !== null || nombreModifications === 0}
            onClick={() => void prévisualiser()}
          >
            {enCours === 'prévisualisation' ? 'Comparaison…' : 'Vérifier les changements'}
          </button>
          {nombreModifications > 0 && (
            <span className="aide">
              {nombreModifications} champ{nombreModifications > 1 ? 's' : ''} modifié
              {nombreModifications > 1 ? 's' : ''}
            </span>
          )}
          {(nombreModifications > 0 || mode === 'texte') && (
            <button
              type="button"
              className="lien"
              disabled={enCours !== null}
              onClick={() => {
                setÉditions({});
                setTexte(source.rawContent);
                setAperçu(null);
              }}
            >
              Réinitialiser
            </button>
          )}
        </div>
      )}

      {aperçu !== null && (
        <div className="carte-interne">
          <h3>Changements à appliquer</h3>

          {aperçu.issues !== undefined && aperçu.issues.length > 0 && (
            <>
              <p className="erreur">Configuration refusée : rien n’a été écrit sur le serveur.</p>
              <ul className="liste-problemes">
                {aperçu.issues.map((problème) => (
                  <li key={problème.path}>
                    <code>{problème.path}</code> — {problème.message}
                  </li>
                ))}
              </ul>
            </>
          )}

          {aperçu.changes !== undefined && aperçu.changes.length === 0 && (
            <p className="aide">Aucun changement : la configuration soumise est identique.</p>
          )}

          {aperçu.warnings !== undefined && aperçu.warnings.length > 0 && (
            <>
              <p className="avertissement">
                La configuration est valide, mais tout ne fonctionnera pas :
              </p>
              <ul className="liste-problemes">
                {aperçu.warnings.map((avertissement) => (
                  <li key={avertissement.message}>{avertissement.message}</li>
                ))}
              </ul>
              <p className="aide">
                Ces points n’empêchent pas d’enregistrer — on met souvent une configuration en place
                en plusieurs fois, en attendant une clé d’API.
              </p>
            </>
          )}

          {aperçu.overriddenPaths !== undefined && aperçu.overriddenPaths.length > 0 && (
            <p className="avertissement">
              Sans effet réel : {aperçu.overriddenPaths.join(', ')} — la valeur appliquée au site
              continuera de venir de l’environnement.
            </p>
          )}

          {aperçu.changes !== undefined && aperçu.changes.length > 0 && (
            <>
              <table className="table-diff">
                <thead>
                  <tr>
                    <th>Clé</th>
                    <th>Avant</th>
                    <th>Après</th>
                  </tr>
                </thead>
                <tbody>
                  {aperçu.changes.map((changement) => (
                    <tr key={changement.path} className={`diff diff--${changement.kind}`}>
                      <td>
                        <code>{changement.path}</code>
                        {changement.masked && <span className="etiquette">secret</span>}
                      </td>
                      <td>{changement.before ?? <em>absente</em>}</td>
                      <td>{changement.after ?? <em>supprimée</em>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="avertissement">
                Appliquer écrit le fichier, conserve une copie horodatée sur le serveur, puis
                redémarre le site. Si le site ne revient pas dans la minute, la configuration
                précédente est rétablie automatiquement.
              </p>
              <div className="actions">
                <button type="button" disabled={enCours !== null} onClick={() => void appliquer()}>
                  {enCours === 'application' ? 'Application…' : 'Appliquer et redémarrer'}
                </button>
                <button
                  type="button"
                  className="lien"
                  disabled={enCours !== null}
                  onClick={() => setAperçu(null)}
                >
                  Annuler
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {peutModifier && (
        <div className="carte-interne">
          <h3>Qui redémarre ce site</h3>
          <p className="aide">
            Une modification de configuration ne prend effet qu’au redémarrage de Ghost. Sur
            certains hébergements — les mutualisés Infomaniak, notamment — l’application Node.js est
            supervisée par le fournisseur : elle ne tourne pas sous le compte SSH, aucune commande
            ne peut la relancer, et le seul redémarrage réel est un bouton dans son panneau. Coche
            alors la case : Vigie écrira la configuration et te dira quoi faire, au lieu de tenter
            une commande qui lancerait un second Ghost se disputant le port.
          </p>
          <div className="case">
            <label>
              <input
                type="checkbox"
                checked={externe}
                onChange={(e) => void enregistrerRéglage(e.target.checked)}
              />
              <span>Le redémarrage se fait hors de Vigie</span>
            </label>
          </div>
          {externe && (
            <>
              <label htmlFor="consigne-redemarrage">Consigne à afficher</label>
              <input
                id="consigne-redemarrage"
                value={consigne}
                onChange={(e) => setConsigne(e.target.value)}
                onBlur={() => void enregistrerRéglage(true)}
              />
              <p className="aide">
                Elle s’affichera après chaque enregistrement, pour qu’on sache où cliquer sans
                chercher.
              </p>
            </>
          )}
          {réglageEnregistré !== null && <p className="succes">{réglageEnregistré}</p>}
        </div>
      )}

      {résultat !== null && (
        <div className="carte-interne">
          <h3>Résultat</h3>
          {résultat.issues !== undefined && résultat.issues.length > 0 ? (
            <ul className="liste-problemes">
              {résultat.issues.map((problème) => (
                <li key={problème.path}>
                  <code>{problème.path}</code> — {problème.message}
                </li>
              ))}
            </ul>
          ) : résultat.rolledBack ? (
            <p className="avertissement">
              Le site n’est pas revenu après le redémarrage : la configuration précédente a été
              rétablie.{' '}
              {résultat.healthy
                ? 'Le site répond de nouveau.'
                : 'Le site ne répond toujours pas — une intervention directe est nécessaire.'}
            </p>
          ) : résultat.pendingRestart ? (
            <>
              <p className="avertissement">
                Configuration <strong>écrite</strong>, mais pas encore chargée par Ghost : le
                redémarrage se fait hors de Vigie.
              </p>
              <p className="aide">{résultat.restartHint}</p>
              <p className="aide">
                Tant que ce redémarrage n’a pas eu lieu, le site continue de tourner avec l’ancienne
                configuration. La révision est enregistrée dans l’historique, et une copie horodatée
                de l’ancien fichier reste sur le serveur.
              </p>
            </>
          ) : (
            <p className="succes">Configuration appliquée, site sain après redémarrage.</p>
          )}
          {résultat.log !== undefined && résultat.log.length > 0 && (
            <details>
              <summary>Journal de l’opération</summary>
              <pre className="journal">{résultat.log.join('\n')}</pre>
            </details>
          )}
        </div>
      )}

      <h3>Historique</h3>
      {révisions === null ? (
        <button type="button" className="lien" onClick={() => void chargerRévisions()}>
          Afficher les révisions
        </button>
      ) : révisions.length === 0 ? (
        <p className="aide">Aucune révision enregistrée pour ce site.</p>
      ) : (
        <ul className="liste-revisions">
          {révisions.map((révision) => (
            <li key={révision.id}>
              <div className="revision-entete">
                <span>{new Date(révision.createdAt).toLocaleString('fr-FR')}</span>
                <span className={révision.applied ? 'etiquette' : 'etiquette etiquette--alerte'}>
                  {révision.applied ? 'appliquée' : 'non appliquée'}
                </span>
                {peutModifier && (
                  <button
                    type="button"
                    className="lien"
                    disabled={enCours !== null}
                    onClick={() => void restaurer(révision.id)}
                  >
                    Restaurer
                  </button>
                )}
              </div>
              <pre className="journal">{révision.diff}</pre>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
